import { RelayPatchCachingServersView } from '../view/RelayPatchCachingServersView';
import { templateRelayPatchCachingServers } from '../view/template';
import { RelayPatchCachingServersRepository } from '../repository/RelayPatchCachingServersRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { RelayPatchCachingServersWebComponent } from '..';
import { DataGridItem } from '../types';
import { CustomRelayValidator } from '../../../ui/gzForm/validators/patch-relay.validator';

export class RelayPatchCachingServersController extends BaseWebComponentController {
  public component: RelayPatchCachingServersWebComponent;
  public dataRepository: RelayPatchCachingServersRepository;
  public view: RelayPatchCachingServersView;
  public viewConstructor: typeof RelayPatchCachingServersView;
  public dataRepositoryConstructor: typeof RelayPatchCachingServersRepository;

  constructor(component: RelayPatchCachingServersWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: RelayPatchCachingServersView,
      viewTemplate: templateRelayPatchCachingServers,
      dataRepositoryConstructor: RelayPatchCachingServersRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$customHostnamesInput,
        event: 'change',
        callback: this.customHostnameInputChange.bind(this),
      },
      {
        element: this.view.dom.$searchInput,
        event: 'selectionChanged',
        callback: this.onSelectedRelayChanged,
      },
      {
        element: this.view.dom.$searchInput,
        event: 'onNewInputString',
        callback: this.searchInputChange.bind(this),
      },
      {
        element: this.view.dom.$addRelayButton,
        event: 'click',
        callback: this.addRelayServerToGrid.bind(this),
      },
      {
        element: this.view.dom.$gridView,
        event: 'deleteRelay',
        callback: this.deleteRelay.bind(this),
      },
      {
        element: this.view.dom.$gridView,
        event: 'upRelay',
        callback: this.upRelay.bind(this),
      },
      {
        element: this.view.dom.$gridView,
        event: 'downRelay',
        callback: this.downRelay.bind(this),
      },
    ];
  }

  onSelectedRelayChanged(ev: WebComponentEvent) {
    const selectedItem = this.view.dom.$searchInput.selectedOption;

    if (this.isValidRelay(selectedItem.name)) {
      this.view.dom.$customHostnamesInput.value = '';
      this.view.dom.$customHostnamesInput.disabled = false;
      this.view.dom.$addRelayButton.disabled = false;
    }
  }

  deleteRelay(ev: WebComponentEvent) {
    const relays = this.component.cachingServers;
    const rowIndex = ev.detail.rowData.index;
    relays.splice(rowIndex, 1);

    this.component.cachingServers = relays;
  }

  upRelay(ev: WebComponentEvent) {
    const rowIndex = ev.detail.rowData.index;

    const relays = this.component.cachingServers;
    const elementsNo = relays.length;

    if (rowIndex === 0) {
      return;
    }

    relays[elementsNo] = relays[rowIndex];
    relays[rowIndex] = relays[rowIndex - 1];
    relays[rowIndex - 1] = relays[elementsNo];

    relays.splice(elementsNo, 1);

    this.component.cachingServers = relays;
  }

  downRelay(ev: WebComponentEvent) {
    const rowIndex = ev.detail.rowData.index;

    const relays = this.component.cachingServers;
    const elementsNo = relays.length;

    if (rowIndex === elementsNo - 1) {
      return;
    }

    relays[elementsNo] = relays[rowIndex];
    relays[rowIndex] = relays[rowIndex + 1];
    relays[rowIndex + 1] = relays[elementsNo];

    relays.splice(elementsNo, 1);

    this.component.cachingServers = relays;
  }

  isValidRelay(relayName: string) {
    const relays = this.component.cachingServers;

    let validRelay = true;
    relays.forEach(item => {
      if (item.name === relayName) {
        validRelay = false;
      }
    });

    return validRelay;
  }

  addRelayServerToGrid() {
    const relays = this.component.cachingServers;
    const selectedItem = this.view.dom.$searchInput.selectedOption;

    // if not valid relay, disable button
    if (!this.isValidRelay(selectedItem)) {
      return;
    }

    const dataGridRelayItem = {} as DataGridItem;
    dataGridRelayItem.protectedEntityId = selectedItem._id;
    dataGridRelayItem.name = selectedItem.name;
    dataGridRelayItem.ip = selectedItem.ip;
    dataGridRelayItem.dnsHostName = selectedItem.specifics.dnsHostName || '';
    dataGridRelayItem.fqdn = selectedItem.specifics.fqdn || '';
    dataGridRelayItem.customHostName = this.view.dom.$customHostnamesInput.value;

    relays.push(dataGridRelayItem);

    this.component.cachingServers = relays;

    // disable button after add
    this.view.dom.$addRelayButton.disabled = true;
    this.view.dom.$customHostnamesInput.disabled = true;
    this.view.dom.$customHostnamesInput.value = '';

    //clear input value after add
    this.clearInputSelector(this.view.dom.$searchInput);
    this.view.dom.$searchInput.localFiltering = false;
  }

  clearInputSelector(selector) {
    selector.lastSelectedOption = null;
    selector.selectedOption = null;
    selector.performClienSideFiltering();

    this.view.dom.$addRelayButton.disabled = true;
    this.view.dom.$customHostnamesInput.disabled = true;

    selector.clearInput();
  }

  customHostnameInputChange(event) {
    const newValue = event.detail.sourceEvent;

    const { valid, error, ip } = CustomRelayValidator(newValue);

    if (error !== '') {
      setTimeout(() => {
        if (ip) {
          this.view.dom.$customHostnamesInput.markAsInvalid(this.dependencies.translateFn(error, { ip }));
        } else {
          this.view.dom.$customHostnamesInput.markAsInvalid(this.dependencies.translateFn(error));
        }
      }, 500);
    } else {
      setTimeout(() => {
        this.view.dom.$customHostnamesInput.markAsValid();
      }, 500);
    }

    this.view.dom.$addRelayButton.disabled = !valid;
  }

  async searchInputChange() {
    this.view.dom.$customHostnamesInput.value = '';
    this.view.dom.$customHostnamesInput.disabled = true;
    this.view.dom.$addRelayButton.disabled = true;

    if (this.component.companyId === '') {
      return;
    }

    const result = await this.dataRepository.getRelayServers({
      name: this.view.dom.$searchInput.value,
      filter: [
        {
          property: 'company.id',
          value: this.component.companyId,
        },
      ],
    });

    const formattedResult = result.data.map(item => {
      const newItem = { ...item, title: item.name };
      return newItem;
    });

    this.view.dom.$searchInput.options = formattedResult;
    this.view.dom.$searchInput.localFiltering = true;
  }

  async getDataForView(): Promise<{ component: RelayPatchCachingServersWebComponent }> {
    const component = this.component;
    return { component };
  }
}
